/**
 * Os documentos de apoio do caso (E98.F5.T07, ADR-0083 §2.6; E205.F4, ADR-0147 §2.5).
 *
 * CNH, verso, nota fiscal, fatura e extrato chegam pelo mesmo envio dos documentos. Cada um ganha,
 * na linha dele da aba Documentos, o cartão em que o perito escolhe o tipo, pede a leitura e confirma
 * os campos (`DocumentoDeApoioCartao`). **O exame só lê a leitura confirmada**: a proposta nunca é
 * gravada. Até a E205.F4 era a seção recolhida "Documentos de apoio".
 *
 * **Só no kit de veículos** (decisão do owner, 2026-09-15), **no consignado do arquivo único**
 * (E106.F3) **e no pacote do banco ou com imagem solta** (E109.F1): fora deles a lista vem vazia, e
 * o Safra e o BMG ficam como estavam.
 *
 * **Troca de caso** (E89.F2.C01): a lista é do caso da tela; resposta que chega de outro caso não
 * escreve.
 */
import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@/lib/api';
import {
  listarDocumentosDeApoio,
  type DocumentoDeApoioData,
  type DocumentosDeApoioDoCasoData,
} from '@/lib/documentos_de_apoio';
import { useTrocaDeCaso } from '@/pages/useTrocaDeCaso';

/** Kit de veículos, arquivo único do consignado ou (E109.F1) pacote do banco e imagem solta. */
export function comApoio(dados: DocumentosDeApoioDoCasoData): boolean {
  return Boolean(
    dados.kit_de_veiculos || dados.consignado_do_arquivo_unico || dados.imagens_do_material,
  );
}

export interface DocumentosDeApoioDoCaso {
  /** Os documentos de apoio; vazio fora dos casos com apoio, antes de ler e quando a leitura falha. */
  documentos: readonly DocumentoDeApoioData[];
  /** A última leitura falhou (a lista anterior, se havia, fica). */
  erro: boolean;
  reler: () => void;
}

export function useDocumentosDeApoio(caseId: string, releitura?: unknown): DocumentosDeApoioDoCaso {
  const client = useApiClient();
  const [dados, setDados] = useState<DocumentosDeApoioDoCasoData | null>(null);
  const [erro, setErro] = useState(false);
  const vigente = useTrocaDeCaso(caseId, () => {
    setDados(null);
    setErro(false);
  });

  const carregar = useCallback(async (): Promise<void> => {
    const pedido = caseId;
    try {
      const lido = await listarDocumentosDeApoio(client, pedido);
      if (vigente.current !== pedido) return;
      setDados(lido);
      setErro(false);
    } catch {
      if (vigente.current === pedido) setErro(true);
    }
  }, [client, caseId, vigente]);

  useEffect(() => {
    void carregar();
  }, [carregar, releitura]);

  const reler = useCallback(() => void carregar(), [carregar]);
  return {
    documentos: dados !== null && comApoio(dados) ? dados.documentos : [],
    erro,
    reler,
  };
}
